import _ from 'lodash';
import { navigate } from 'raviger';
import React, { Suspense, useEffect, useState } from 'react';
import { InputGroup, Panel } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import FormInput from '../../components/FormInput/FormInput';
import Permission from '../../components/Permission/Permission';
import Spinner from '../../components/Spinner/Spinner';
import * as constants from '../../constants';
import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import OptionsHelper from '../../lib/OptionsHelper';
import { getListState, saveListState } from '../../lib/ListStateHelper';
import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import { useAlertStore } from '../../store/providers/Alert';
import { useSurveyTemplateCategoryStore } from '../../store/providers/SurveyTemplateCategories';
import { useSurveyTemplatesStore } from '../../store/providers/SurveyTemplates';
import { columns, sorting } from './SurveyTemplateListSchema';

const DocumentSetupList = React.lazy(() => import('../../components/Lists/DocumentSetupList/DocumentSetupList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function SurveyTemplates() {
    const dispatch = useDispatch();
    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useSurveyTemplatesStore();
    const { listRecords: listCategoryData, data: categoryData } = useSurveyTemplateCategoryStore();

    const listKey = '/surveyTemplates';
    const savedListState = getListState(listKey) || {};

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(savedListState.categoryId || '');
    const pagePermission = constants.PERMISSION_CONTENT_SURVEYTEMPLATES;

    const selectedHandler = (surveyTemplateItem) => {
        navigate(`/surveyTemplate/edit/${surveyTemplateItem.id}`);
    };

    const addedHandler = () => {
        navigate(`/surveyTemplate/new`);
    };

    const deletedHandler = (surveyTemplateItem) => {
        deleteRecord(surveyTemplateItem.id);
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const moreFilters = (categoryOptions) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Category:</InputGroup.Addon>
                        <FormInput id="categoryId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedCategory}
                        >
                            {categoryOptions}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }

    const changedHandler = (id, value) => {
        if (id === 'categoryId') {
            const categoryId = value ? parseInt(value) : '';
            setSelectedCategory(categoryId);
            saveListState(listKey, { categoryId });
            listRecords();
        }
    }

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'ssTemplates', parent: 'Content', text: 'Survey Templates', path: window.location.pathname }));

        listRecords();
        listCategoryData();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                if (selectedCategory) {
                    const listDataFiltered = data.body.filter((item) => item.categoryId === selectedCategory);
                    setListData(listDataFiltered);
                } else {
                    setListData(data.body);
                }
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords, selectedCategory]);

    useEffect(() => {
        if (!_.isEmpty(categoryData) && categoryData.scope === 'list' && categoryData.status === 'success') {
            setCategoryOptions(OptionsHelper({ list: categoryData.body, keyField: 'id', nameField: 'name', defaultOptions: [['', 'All']], includeBlank: false }));
        }
    }, [categoryData]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="content" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted && categoryOptions &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Survey Templates" />
                            <Panel>
                                <Panel.Body>
                                    <DocumentSetupList
                                        compType="Survey Template"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideRefresh={true}
                                        hideFilter={true}
                                        moreFilters={moreFilters(categoryOptions)}
                                        added={() => addedHandler()}
                                        selected={(surveyTemplate) => selectedHandler(surveyTemplate)}
                                        deleted={(surveyTemplate) => deletedHandler(surveyTemplate)}
                                        refreshed={() => refreshedHandler()}
                                        permissionEdit={canEdit}
                                    />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(SurveyTemplates));
